import { useState, useEffect } from 'react';
import { useUser } from '@clerk/react';
import { Button, Form, Alert } from 'react-bootstrap';
import { Locale } from '../i18n/types';
import { Lang } from '../i18n';
import FtpSlider from './sliders/FtpSlider';
import WeightSlider from './sliders/WeightSlider';
import BikeWeightSlider from './sliders/BikeWeightSlider';
import MaxAbsorptionSlider from './sliders/MaxAbsorptionSlider';
import SafetyMarginSlider from './sliders/SafetyMarginSlider';

interface Props {
  locale: Locale;
  lang: Lang;
  setLang: (lang: Lang) => void;
}

export default function AccountSettings({ locale, lang, setLang }: Props) {
  const { user } = useUser();
  const [ftp, setFtp] = useState<number>(200);
  const [weight, setWeight] = useState<number>(75);
  const [bikeWeight, setBikeWeight] = useState<number>(8.0);
  const [maxAbsorption, setMaxAbsorption] = useState<number>(60);
  const [safetyMargin, setSafetyMargin] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (user) {
      const meta = user.unsafeMetadata as { ftp?: number; weight?: number; bikeWeight?: number; maxAbsorption?: number; safetyMargin?: number } | undefined;
      setFtp(meta?.ftp || 200);
      setWeight(meta?.weight || 75);
      setBikeWeight(meta?.bikeWeight || 8.0);
      setMaxAbsorption(meta?.maxAbsorption || 60);
      setSafetyMargin(meta?.safetyMargin ?? 0);
    }
  }, [user]);

  const handleSave = async () => {
    if (!user || !ftp || !weight) {
      setMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await user.update({
        unsafeMetadata: {
          ...(user.unsafeMetadata || {}),
          ftp,
          weight,
          bikeWeight,
          maxAbsorption,
          safetyMargin,
        },
      });
      setMessage({ type: 'success', text: locale.settings.saved });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save profile' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100%' }} className="d-flex flex-column gap-4">
      <div>
        <h2 className="tab-page-title">{locale.nav.settings}</h2>
      </div>

      <div className="border-top pt-4 divider-dark">
        <h4 className="section-heading">{locale.settings.language.heading}</h4>
        <div className="d-flex gap-2">
          <Button
            variant={lang === 'pl' ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={async () => {
              setLang('pl');
              if (user) {
                try {
                  await user.update({
                    unsafeMetadata: {
                      ...(user.unsafeMetadata || {}),
                      lang: 'pl',
                    },
                  });
                } catch (err) {
                  console.error('Failed to save language preference:', err);
                }
              }
            }}
          >
            Polski
          </Button>
          <Button
            variant={lang === 'en' ? 'primary' : 'outline-secondary'}
            size="sm"
            onClick={async () => {
              setLang('en');
              if (user) {
                try {
                  await user.update({
                    unsafeMetadata: {
                      ...(user.unsafeMetadata || {}),
                      lang: 'en',
                    },
                  });
                } catch (err) {
                  console.error('Failed to save language preference:', err);
                }
              }
            }}
          >
            English
          </Button>
        </div>
      </div>

      <div className="border-top pt-4 divider-dark">
        <h4 className="section-heading">{locale.settings.cyclist.heading}</h4>
        {message && (
          <Alert variant={message.type} className="mb-3">
            {message.text}
          </Alert>
        )}
        <Form className="d-flex flex-column gap-3">
          <FtpSlider
            value={ftp}
            onChange={setFtp}
            disabled={loading}
          />
          <WeightSlider
            value={weight}
            onChange={setWeight}
            disabled={loading}
          />
          <MaxAbsorptionSlider
            value={maxAbsorption}
            onChange={setMaxAbsorption}
            disabled={loading}
          />
          <SafetyMarginSlider
            value={safetyMargin}
            onChange={setSafetyMargin}
            disabled={loading}
          />
        </Form>
      </div>

      <div className="border-top pt-4 divider-dark">
        <h4 className="section-heading">{locale.settings.bike.heading}</h4>
        <Form className="d-flex flex-column gap-3">
          <BikeWeightSlider
            value={bikeWeight}
            onChange={setBikeWeight}
            disabled={loading}
          />
        </Form>
      </div>

      <Button
        variant="primary"
        onClick={handleSave}
        disabled={loading || !ftp || !weight}
        className="mt-2"
      >
        {loading ? locale.settings.saving : locale.settings.save}
      </Button>
    </div>
  );
}
